import { ActivatedRouteSnapshot, ParamMap } from '@angular/router';

export type EmailVerificationCredentials = {
  id: string | null;
  hash: string | null;
  signedParams: Record<string, string>;
};

const SIGNED_QUERY_KEYS = ['expires', 'signature'] as const;

export function readEmailVerificationCredentials(
  params: ParamMap,
  queryParams: ParamMap
): EmailVerificationCredentials {
  const id =
    params.get('id') ||
    queryParams.get('id') ||
    queryParams.get('user_id') ||
    queryParams.get('user');

  const hash = params.get('hash') || queryParams.get('hash');

  const signedParams: Record<string, string> = {};
  for (const key of SIGNED_QUERY_KEYS) {
    const value = queryParams.get(key);
    if (value) {
      signedParams[key] = value;
    }
  }

  return { id, hash, signedParams };
}

export function readEmailVerificationCredentialsFromSnapshot(
  snapshot: ActivatedRouteSnapshot
): EmailVerificationCredentials {
  return readEmailVerificationCredentials(snapshot.paramMap, snapshot.queryParamMap);
}

export function hasEmailVerificationCredentials(
  credentials: EmailVerificationCredentials
): boolean {
  return !!credentials.id && !!credentials.hash;
}

export function isEmailAlreadyVerifiedMessage(message: string): boolean {
  return /already verified|email.*verified|déjà vérifi/i.test(message);
}

export function extractApiErrorMessage(error: unknown): string {
  const candidate = error as {
    error?: { message?: string; errors?: Record<string, string[] | string> };
    message?: string;
  };

  if (candidate?.error?.message) {
    return candidate.error.message;
  }

  const errors = candidate?.error?.errors;
  if (errors) {
    const firstKey = Object.keys(errors)[0];
    const firstValue = firstKey ? errors[firstKey] : undefined;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return firstValue[0];
    }
    if (typeof firstValue === 'string') {
      return firstValue;
    }
  }

  return candidate?.message || '';
}

export function isUserEmailVerified(user: any): boolean {
  if (!user) return false;
  if (typeof user.is_verified === 'boolean') {
    return user.is_verified;
  }
  if (typeof user.email_verified === 'boolean') {
    return user.email_verified;
  }
  if ('email_verified_at' in user) {
    const value = user.email_verified_at;
    if (value === null || value === undefined || value === false) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return Boolean(value);
  }
  // Missing verification flags → treat as unverified (fail-closed).
  return false;
}

export function hasUserAlreadyVerifiedEmail(user: any): boolean {
  if (!user) return false;
  if (user.is_verified === true || user.email_verified === true) {
    return true;
  }
  if (user.email_verified_at && typeof user.email_verified_at === 'string' && user.email_verified_at.trim() !== '') {
    return true;
  }
  return false;
}
